import { DossierDetailsContent, DossierDetailsHeader, DossierDetailsPanel } from "@/app/pages/dossier/dossier-details-panel";
import { PeriodeFactor, Reisperiode, Rijtijd, TvvStandaardRoute, usePostTvvBestellingen } from "@/entities/tvv";
import { useBestelTVVRoutes } from "@/features/tvv/components/regievoerder/bestel-routes-context.ts";
import { ErrorDisplay } from "@/shared/components/error";
import { Button, Form } from "@/shared/components/ui";
import type { TvvRouteAanvraagDto } from "@/shared/types/generated/dtos";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { RouteBestellingItem } from "./route-bestelling-item";
import { BestellingenFormData, bestellingenSchema } from "./schema";

type BestellingViewProps = {
    dossierId: string;
};

export function BestellingView({ dossierId }: Readonly<BestellingViewProps>) {
    const { selectedRoutes, setMode, clearSelectedRoutes, makeRouteAvailable } = useBestelTVVRoutes();
    const [submitError, setSubmitError] = useState<Error | null>(null);
    const postBestellingen = usePostTvvBestellingen();

    const form = useForm<BestellingenFormData>({
        resolver: zodResolver(bestellingenSchema),
        defaultValues: {
            bestellingen: selectedRoutes.map((route) => {
                const reisperiode = PeriodeFactor.getCurrentReisperiode(route?.periodeFactoren ?? []);
                const periodeFactor = TvvStandaardRoute.getPeriodeFactor(route, reisperiode);
                const rijtijd = Rijtijd.getTotaleRijtijd(route.rijtijden) ?? -1;
                const initWaarden = periodeFactor
                    ? PeriodeFactor.getInitieleBestelWaarden(periodeFactor, rijtijd)
                    : { busFrequentie: 0, aantalBussen: 0, percentageBeginStation: 0, bussenBeginStation: 0, bussenEindStation: 0 };
                return {
                    routeId: route.routeId,
                    voorwaardeGeaccepteerd: !route.voorwaarde,
                    bestelling: {
                        reisperiode,
                        ...initWaarden,
                        isAangepast: false,
                    },
                };
            }),
        },
    });

    const { fields } = useFieldArray({
        control: form.control,
        name: "bestellingen",
    });

    /* When route is edited, we need to recalculate the form values */
    const onEditUpdateForm = (updatedRoute: TvvStandaardRoute, index: number) => {
        if (updatedRoute.isOvRoute) return;

        const path = `bestellingen.${index}.bestelling` as const;
        const reisperiode = form.getValues(`${path}.reisperiode`);
        const periodeFactor = TvvStandaardRoute.getPeriodeFactor(updatedRoute, reisperiode);
        const nieuweRijtijd = Rijtijd.getTotaleRijtijd(updatedRoute.rijtijden) ?? -1;

        if (!periodeFactor || nieuweRijtijd <= 0) return;

        const initWaarden = PeriodeFactor.getInitieleBestelWaarden(periodeFactor, nieuweRijtijd);
        form.setValue(`${path}.busFrequentie`, initWaarden.busFrequentie);
        form.setValue(`${path}.aantalBussen`, initWaarden.aantalBussen);
        form.setValue(`${path}.bussenBeginStation`, initWaarden.bussenBeginStation);
        form.setValue(`${path}.bussenEindStation`, initWaarden.bussenEindStation);
        form.setValue(`${path}.percentageBeginStation`, initWaarden.percentageBeginStation);
        form.setValue(`${path}.isAangepast`, false);
    };

    const handleSubmit = form.handleSubmit(async (data) => {
        setSubmitError(null);

        const heeftOngeaccepteerdeVoorwaarden = data.bestellingen.some((b, i) => selectedRoutes[i].voorwaarde && !b.voorwaardeGeaccepteerd);
        const isOvRouteZonderLijnNummer = data.bestellingen.some((b, i) => selectedRoutes[i].isOvRoute && !b.bestelling.ovLijnNummer);

        if (heeftOngeaccepteerdeVoorwaarden) {
            data.bestellingen.forEach((b, i) => {
                if (selectedRoutes[i].voorwaarde && !b.voorwaardeGeaccepteerd) {
                    form.setError(`bestellingen.${i}.voorwaardeGeaccepteerd`, {
                        message: "Accepteer de voorwaarde om te bestellen",
                    });
                }
            });
            return;
        }

        if (isOvRouteZonderLijnNummer) {
            data.bestellingen.forEach((b, i) => {
                if (selectedRoutes[i].isOvRoute && !b.bestelling.ovLijnNummer) {
                    form.setError(`bestellingen.${i}.bestelling.ovLijnNummer`, {
                        message: "Vul een lijnnummer in",
                    });
                }
            });
            return;
        }

        const aanvragen = data.bestellingen.map((b, i): TvvRouteAanvraagDto => {
            const selectedRoute = selectedRoutes[i];
            const isNieuweRoute = TvvStandaardRoute.isNieuweRoute(selectedRoute.routeId);
            return {
                aantalBussenOpBeginStation: selectedRoute.isOvRoute ? 0 : b.bestelling.bussenBeginStation,
                busFrequentiePerUur: selectedRoute.isOvRoute ? 0 : b.bestelling.busFrequentie,
                bussenBesteld: selectedRoute.isOvRoute ? 0 : b.bestelling.aantalBussen,
                organisatie: selectedRoute.organisatie,
                reisperiode: isNieuweRoute ? Reisperiode.Handmatig : b.bestelling.reisperiode,
                routeSoort: selectedRoute.routeSoort,
                routeStationCodes: selectedRoute.stationCodes,
                standaardRouteId: selectedRoute.routeId,
                voertuigSoort: selectedRoute.voertuigSoort,
                ovLijnNummer: b.bestelling.ovLijnNummer,
                rijtijden: selectedRoute.rijtijden,
            };
        });

        try {
            await postBestellingen.mutateAsync({ dossierId, bestellingen: aanvragen });
            clearSelectedRoutes();
            setMode("overzicht");
        } catch (error) {
            setSubmitError(error as Error);
        }
    });

    return (
        <DossierDetailsPanel>
            <DossierDetailsHeader section="Tvv" title="Route bestellen" showBackButton onBackClick={() => setMode("selecteren")} />
            <DossierDetailsContent>
                <Form {...form}>
                    <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
                        {submitError && <ErrorDisplay error={submitError} title="Fout bij het versturen van de bestelling" />}

                        <div className="flex flex-col border-b">
                            {fields
                                .filter((_, index) => !!selectedRoutes[index])
                                .map((field, index) => (
                                    <RouteBestellingItem
                                        key={field.id}
                                        route={selectedRoutes[index]}
                                        index={index}
                                        form={form}
                                        defaultOpen
                                        onRouteEdited={(updatedRoute) => {
                                            makeRouteAvailable(updatedRoute, true, selectedRoutes[index].routeId);
                                            onEditUpdateForm(updatedRoute, index);
                                        }}
                                    />
                                ))}
                        </div>

                        <div>
                            <Button variant="primary" type="submit" disabled={postBestellingen.isPending}>
                                Bestelling versturen
                            </Button>
                        </div>
                    </form>
                </Form>
            </DossierDetailsContent>
        </DossierDetailsPanel>
    );
}
